import React, { useEffect, useRef } from 'react';

const BrandingPreview = ({ brandColor, fontFamily, logoUrl, companyName, companyWebsite, privacyPolicyUrl, termsAndConditionsUrl, height = 600 }) => {
  const iframeRef = useRef(null);

  const injectBranding = () => {
    try {
      const iframe = iframeRef.current;
      if (!iframe) return;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      const familyRaw = (fontFamily || '').split(',')[0].replace(/['"]/g, '').trim();
      if (familyRaw) {
        const gfId = `gf-${familyRaw.replace(/\s+/g, '-')}`;
        if (!doc.getElementById(gfId)) {
          const link = doc.createElement('link');
          link.id = gfId;
          link.rel = 'stylesheet';
          const urlFamily = `${familyRaw.replace(/\s+/g, '+')}:wght@300;400;500;600;700`;
          link.href = `https://fonts.googleapis.com/css2?family=${urlFamily}&display=swap`;
          doc.head.appendChild(link);
        }
      }

      // Inject override styles
      const styleId = 'branding-preview-overrides';
      let styleTag = doc.getElementById(styleId);
      if (!styleTag) {
        styleTag = doc.createElement('style');
        styleTag.id = styleId;
        doc.head.appendChild(styleTag);
      }

      const color = brandColor || '#2fbaad';
      const family = fontFamily || 'Questrial, sans-serif';

      const hexToRgb = (hex) => {
        const h = hex.replace('#', '');
        if (h.length === 3) {
          const r = parseInt(h[0] + h[0], 16);
          const g = parseInt(h[1] + h[1], 16);
          const b = parseInt(h[2] + h[2], 16);
          return `${r}, ${g}, ${b}`;
        }
        if (h.length === 6) {
          const r = parseInt(h.substring(0, 2), 16);
          const g = parseInt(h.substring(2, 4), 16);
          const b = parseInt(h.substring(4, 6), 16);
          return `${r}, ${g}, ${b}`;
        }
        return '47, 186, 173';
      };
      const colorRgb = hexToRgb(color);

      styleTag.textContent = `
        :root { --branding-theme-color: ${color} !important; --theme-color: ${color} !important; --skin1: ${color} !important; }
        body { font-family: ${family} !important; }
        a { color: ${color} !important; }
        .btn-primary, .btn-success, .btn-info { background-color: ${color} !important; border-color: ${color} !important; }
        .btn-outline-primary, .btn-outline-success, .btn-outline-info { color: ${color} !important; border-color: ${color} !important; }
        .btn-outline-primary:hover, .btn-outline-success:hover, .btn-outline-info:hover { background-color: ${color} !important; color: #ffffff !important; border-color: ${color} !important; }
        .text-mmddarkblue { color: ${color} !important; }
        .navbarbg, .topbar, [data-navbarbg], [data-logobg] { background-color: ${color} !important; }
        /* Sidebar accents */
        #sidebarbg .sidebar-nav .sidebar-item .sidebar-link { color: ${color} !important; }
        #sidebarbg .sidebar-nav .sidebar-item .sidebar-link i { color: ${color} !important; }
        #sidebarbg .sidebar-nav .sidebar-item .sidebar-link.active,
        #sidebarbg .sidebar-nav .sidebar-item .sidebar-link:hover,
        #sidebarbg .sidebar-nav .sidebar-item.selected > .sidebar-link,
        #sidebarbg .sidebar-nav .sidebar-item .sidebar-link:focus { color: ${color} !important; }
        #sidebarbg .sidebar-nav .sidebar-item .sidebar-link.active i,
        #sidebarbg .sidebar-nav .sidebar-item .sidebar-link:hover i,
        #sidebarbg .sidebar-nav .sidebar-item.selected > .sidebar-link i { color: ${color} !important; }
        #sidebarbg .sidebar-nav .sidebar-item .sidebar-link.active { background-color: rgba(${colorRgb}, 0.08) !important; border-left: 3px solid ${color} !important; }
        #sidebarbg .nav-small-cap i, #sidebarbg .nav-small-cap .hide-menu { color: ${color} !important; }
        /* Table and badges */
        .badge-primary, .badge-info, .badge-success { background-color: ${color} !important; }
        .page-breadcrumb .breadcrumb .breadcrumb-item a { color: ${color} !important; }
        .page-wrapper .page-content, .card .card-title { font-family: ${family} !important; }
        /* Pagination */
        .pagination .page-link { color: ${color} !important; border-color: rgba(${colorRgb}, 0.3) !important; }
        .pagination .page-link:hover { color: ${color} !important; }
        .pagination .page-item.active .page-link { background-color: ${color} !important; border-color: ${color} !important; color: #ffffff !important; }
        /* Forms: checkbox/radio/focus */
        .form-check-input:checked { background-color: ${color} !important; border-color: ${color} !important; }
        .form-check-input:focus { border-color: rgba(${colorRgb}, 0.5) !important; box-shadow: 0 0 0 .2rem rgba(${colorRgb}, .25) !important; }
        .custom-control-input:checked ~ .custom-control-label::before { background-color: ${color} !important; border-color: ${color} !important; }
        .custom-control-input:focus ~ .custom-control-label::before { box-shadow: 0 0 0 .2rem rgba(${colorRgb}, .25) !important; border-color: rgba(${colorRgb}, .5) !important; }
        .form-control:focus { border-color: rgba(${colorRgb}, 0.5) !important; box-shadow: 0 0 0 .2rem rgba(${colorRgb}, .25) !important; }
        /* Spinner */
        .spinner-border, .spinner-grow { color: ${color} !important; }
        .spinner-border { border-top-color: ${color} !important; }
        .releaseSpinner, .tableHelper .spinner-border, .tableHelper .spinner-grow { color: ${color} !important; border-top-color: ${color} !important; }
        /* Table helper text */
        .tableHelper p { color: ${color} !important; }
        /* About menu profile button (mmdelectricpurple maps to skin) */
        .btn-mmdelectricpurple, .btn-mmddarkblue, .btn-mmdlitepurple { background-color: ${color} !important; border-color: ${color} !important; }
        .btn-mmdelectricpurple:hover, .btn-mmddarkblue:hover, .btn-mmdlitepurple:hover { background-color: ${color} !important; border-color: ${color} !important; opacity: .9; }
        .user-dd .with-arrow > span { background-color: ${color} !important; }
        /* Hide Zapier interfaces chatbot embed only inside preview */
        zapier-interfaces-chatbot-embed,
        #zapier-interfaces-chatbot-embed,
        .zapier-interfaces-chatbot-embed,
        [id*="zapier-interfaces-chatbot-embed"] {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `;

      // Swap logo if available
      if (logoUrl) {
        const imgs = doc.getElementsByClassName('mmLogo');
        if (imgs && imgs.length > 0) {
          Array.from(imgs).forEach((img) => {
            img.setAttribute('src', logoUrl);
            img.style.objectFit = 'contain';
          });
        }
      }

      // Update footer company name/website and policy links if provided
      try {
        const footer = doc.querySelector('footer.footer');
        if (footer) {
          const links = footer.getElementsByTagName('a');
          if (links && links.length > 0) {
            const companyAnchor = links[0];
            if (companyName && companyName.trim()) {
              companyAnchor.textContent = companyName + ' ';
            }
            if (companyWebsite && companyWebsite.trim()) {
              companyAnchor.setAttribute('href', companyWebsite);
            }
            const anchorsArr = Array.from(links);
            const privacyAnchor = anchorsArr.find(a => (a.textContent || '').toLowerCase().includes('privacy')) || anchorsArr[1];
            const termsAnchor = anchorsArr.find(a => (a.textContent || '').toLowerCase().includes('terms')) || anchorsArr[2];
            if (privacyPolicyUrl && privacyAnchor) {
              privacyAnchor.setAttribute('href', privacyPolicyUrl);
            }
            if (termsAndConditionsUrl && termsAnchor) {
              termsAnchor.setAttribute('href', termsAndConditionsUrl);
            }
          }
        }
      } catch (_) {}

      // Make preview non-interactive to avoid affecting user session (e.g., logout)
      const blockEvent = (e) => {
        try {
          const target = e.target;
          if (!target) return;
          const interactive = target.closest && target.closest('a, button, .btn, [role="button"], input[type="submit"], .nav-link, .sidebar-link, .dropdown-item');
          if (interactive) {
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
            return false;
          }
        } catch (_) {}
      };

      doc.removeEventListener('click', blockEvent, true);
      doc.addEventListener('click', blockEvent, true);
      doc.removeEventListener('submit', blockEvent, true);
      doc.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        return false;
      }, true);

      // Proactively hide Zapier chatbot and related iframes as they are injected
      const hide3p = () => {
        try {
          const sel = [
            'zapier-interfaces-chatbot-embed',
            '#zapier-interfaces-chatbot-embed',
            '.zapier-interfaces-chatbot-embed',
            '[id*="zapier-interfaces-chatbot-embed"]',
          ].join(',');
          const nodes = doc.querySelectorAll(sel);
          nodes.forEach((n) => {
            n.style.setProperty('display', 'none', 'important');
            n.style.setProperty('visibility', 'hidden', 'important');
            n.style.setProperty('pointer-events', 'none', 'important');
          });
        } catch (_) {}
      };
      hide3p();
      if (doc.__brandingPreviewObserver) {
        try { doc.__brandingPreviewObserver.disconnect(); } catch (_) {}
      }
      const mo = new MutationObserver(() => hide3p());
      mo.observe(doc.body || doc.documentElement, { childList: true, subtree: true });
      doc.__brandingPreviewObserver = mo;
    } catch (e) {
      // noop
    }
  };

  useEffect(() => {
    const tid = setTimeout(injectBranding, 50);
    return () => clearTimeout(tid);
  }, [brandColor, fontFamily, logoUrl, companyName, companyWebsite, privacyPolicyUrl, termsAndConditionsUrl]);

  return (
    <div style={{ border: '1px solid #e9ecef', borderRadius: '8px', overflow: 'hidden' }}>
      <iframe
        ref={iframeRef}
        title="Branding Preview"
        src="/"
        style={{ width: '100%', height: `${height}px`, border: '0' }}
        onLoad={injectBranding}
        sandbox="allow-same-origin allow-scripts"
      />
    </div>
  );
};

export default BrandingPreview;


